import { getEnvVars } from '@environment';
import { ClipboardPaste, Copy } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@unistocks/components/button/button';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Column } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { H4, Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { SharedPortfolioSettingsForm } from '@unistocks/features/portfolios/components/sharable-links/shared-portfolio-settings-form';
import {
  setSharedPortfoliosGetSharedPortfolioSettingsData,
  sharedPortfoliosGetSharedPortfolioSettingsQueryKey,
  useSharedPortfoliosCreateOrUpdateSharedPortfolioMutation,
  useSharedPortfoliosGetSharedPortfolioSettingsQuery,
  useSharedPortfoliosRevokeShareAccessMutation
} from '@unistocks/proxy/axios-client/SharedPortfoliosQuery';
import { useDevToolsEnabled } from '@unistocks/shared/hooks/use-feature';
import { t } from '@unistocks/shared/localization/index.ts';
import { Logger } from '@unistocks/shared/services/logger.service';
import { isDefined } from '@unistocks/shared/utils/util';
import { getStringAsync, setStringAsync } from 'expo-clipboard';
import { useRouter } from 'expo-router';
import React, { useCallback } from 'react';
import { ListItem } from 'tamagui';

const { universalLinkBaseUrl } = getEnvVars();

export type SharableLinkProps = {
  portfolioId: string;
};

export const SharableLink: React.FC<SharableLinkProps> = ({ portfolioId }) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const isDevToolsEnabled = useDevToolsEnabled();

  const {
    data: sharedPortfolio,
    isPending: isLoadingSharedPortfolio,
    isSuccess: isSuccessSharedPortfolio,
    refetch: refetchSharedPortfolio
  } = useSharedPortfoliosGetSharedPortfolioSettingsQuery(
    { id: portfolioId },
    {
      retry: false
    }
  );
  const link = `${universalLinkBaseUrl}/share?accept=${sharedPortfolio?.id}`;

  const { mutateAsync: createOrUpdateSharedPortfolio, isPending: isSharePortfolioPending } =
    useSharedPortfoliosCreateOrUpdateSharedPortfolioMutation(portfolioId, {
      onSuccess: res => {
        if (!isDefined(sharedPortfolio)) {
          toast.success(t('::Portfolio:Share:Activate:Success'));
        } else {
          toast.success(t('::Settings:Update:Success'));
        }

        setSharedPortfoliosGetSharedPortfolioSettingsData(queryClient, () => res, portfolioId);
      },
      onError: e => {
        Logger.error(e, 'Error creating/updating shared portfolio:');
      },
      onSettled: () => refetchSharedPortfolio()
    });

  const { mutate: revokeSharedAccess, isPending: isDeleteSharedPortfolioPending } =
    useSharedPortfoliosRevokeShareAccessMutation(portfolioId, {
      onSuccess: () => {
        queryClient.removeQueries({
          queryKey: sharedPortfoliosGetSharedPortfolioSettingsQueryKey(portfolioId)
        });
        toast.success(t('::Portfolio:Share:Disable:Success'));
      }
    });

  const activatePortfolioSharing = useCallback(() => {
    createOrUpdateSharedPortfolio({ includeHistory: false, includeNominalValues: false });
  }, [createOrUpdateSharedPortfolio]);

  const acceptShare = async () => {
    const string = await getStringAsync();

    if (string.startsWith(universalLinkBaseUrl) && URL.canParse(string)) {
      const url = new URL(string);

      return router.navigate({
        pathname: '/share',
        params: { accept: url!.searchParams.get('accept') }
      });
    }

    toast.error('Länken du kopierade är inte giltig', { description: string });
  };

  if (isSuccessSharedPortfolio) {
    return (
      <Column gap={'$3'}>
        <Paragraph>{t('::Portfolio:Share:Link:Description')}</Paragraph>
        <ListItem
          cursor="pointer"
          gap={'$3'}
          borderWidth={1}
          br={'$true'}
          mt={'$3'}
          title={link}
          iconAfter={Copy}
          scaleIcon={1.2}
          onPress={async () => {
            const copied = await setStringAsync(link);
            if (copied) {
              toast.success(t('::Portfolio:Share:Link:Copied'));
            } else {
              toast.error(t('::Portfolio:Share:Link:CopyFailed'));
            }
          }}
        />
        {isDevToolsEnabled ? (
          <>
            <H4>Acceptera delad portfölj</H4>
            <ListItem
              cursor="pointer"
              gap={'$3'}
              borderWidth={1}
              br={'$true'}
              iconAfter={ClipboardPaste}
              scaleIcon={1.2}
              onPress={acceptShare}
            >
              <ListItem.Text numberOfLines={10}>
                {'Acceptera delad portfölj genom att kopiera länken och klicka här'}
              </ListItem.Text>
            </ListItem>
          </>
        ) : null}
        <SharedPortfolioSettingsForm
          initialValues={sharedPortfolio.settings}
          onSubmit={createOrUpdateSharedPortfolio}
        />
        <Confirm
          message={t('::Portfolio:Share:Disable:Confirm')}
          isLoading={isDeleteSharedPortfolioPending}
          onConfirm={revokeSharedAccess}
        >
          <Button theme={'red'}>{t('::Portfolio:Share:Disable')}</Button>
        </Confirm>
      </Column>
    );
  }

  if (isLoadingSharedPortfolio) {
    return <Loader>{t('::Portfolio:Share:Loading')}</Loader>;
  }

  if (!isLoadingSharedPortfolio && !sharedPortfolio) {
    return (
      <Column gap={'$3'}>
        <Paragraph>{t('::Portfolio:Share:Description')}</Paragraph>
        <Button onPress={activatePortfolioSharing} loading={isSharePortfolioPending}>
          {isSharePortfolioPending
            ? t('::Portfolio:Share:Activate:Pending')
            : t('::Portfolio:Share:Activate')}
        </Button>
      </Column>
    );
  }
};
